import Vue from 'vue';
import _ from 'lodash';
import prettyBytes from 'pretty-bytes';
import { parse, format, getUnixTime } from 'date-fns';
import { appNow, appGetUnixTime, appFromUnixTime } from '@/utils/timezone';
import { activeDateLocale } from '@/utils/dateLocale';

import i18nCountries from 'i18n-iso-countries';
i18nCountries.registerLocale(require('i18n-iso-countries/langs/en.json'));
i18nCountries.registerLocale(require('i18n-iso-countries/langs/de.json'));

import i18nLanguages from '@cospired/i18n-iso-languages';
i18nLanguages.registerLocale(require('@cospired/i18n-iso-languages/langs/en.json'));
i18nLanguages.registerLocale(require('@cospired/i18n-iso-languages/langs/de.json'));

import languages from '@/languages';

export const country = (code, language = 'de') => {
    return i18nCountries.getName(code, language) || code;
};
Vue.filter('country', country);

export const language = (code, language = 'de') => {
    return i18nLanguages.getName(code, language) || code;
};
Vue.filter('language', language);

export const languageFlag = (code) => {
    return languages[code].flag;
};
Vue.filter('languageFlag', languageFlag);

export const languageNativeName = (code) => {
    return languages[code].name;
};
Vue.filter('languageNativeName', languageNativeName);

export const currencySymbol = (currency) => {
    const language = window.navigator.userLanguage || window.navigator.language;
    return (0)
        .toLocaleString(language, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim();
};
export const moneyFormat = (value, currency = 'EUR', hideDecimalsIfZero = false) => {
    const language = window.navigator.userLanguage || window.navigator.language;
    let formatted = Intl.NumberFormat(language, { style: 'currency', currency }).format(value);

    if (hideDecimalsIfZero) {
        // Check if value is an integer or has .00 decimals
        if (Number(value) === Math.floor(Number(value))) {
            // Remove decimal part (e.g., ",00" or ".00") from formatted string
            formatted = formatted.replace(/([,.]00)(?!\d)/, '');
        }
    }
    return formatted;
};
Vue.filter('moneyFormat', moneyFormat);

/**
 * Money at full precision for UNIT prices, which are routinely sub-cent (an AI
 * image edit is 0,0180 €). Two decimals would collapse several distinct model
 * prices onto the same figure and make a per-unit price look like a total, so
 * up to four decimals are kept. Totals keep using moneyFormat — that is the
 * amount actually charged.
 */
export const preciseMoneyFormat = (value, currency = 'EUR') => {
    const language = window.navigator.userLanguage || window.navigator.language;

    return Intl.NumberFormat(language, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(Number(value) || 0);
};
Vue.filter('preciseMoneyFormat', preciseMoneyFormat);

export { prettyBytes };
Vue.filter('prettyBytes', prettyBytes);

/**
 * Vue filter to round the decimal to the given place.
 * http://jsfiddle.net/bryan_k/3ova17y9/
 *
 * @param {String} value    The value string.
 * @param {Number} decimals The number of decimal places.
 */
Vue.filter('round', (value = 0, decimals = 0) => Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals));

Vue.filter('truncate', (text, stop, clamp) => {
    return text.slice(0, stop) + (stop < text.length ? clamp || '...' : '');
});
// import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
// const timeZone = 'Europe/Berlin';

// Vue.filter('capitalize', function (value) {
//     if (!value) return ''
//     value = value.toString()
//     return value.charAt(0).toUpperCase() + value.slice(1)
// })
// Vue.filter('timestamp2date', function (value) {
//         if (value == 0) return '-'
//         return format(new Date(value * 1000), "DD.MM.YYYY");
//     })
// Vue.filter('date2timestamp', function (value) {
//     let timeArr = value.split("."),
//         newDate = timeArr[1] + "/" + timeArr[0] + "/" + timeArr[2];
//     return new Date(newDate).getTime();
// })

export const capitalize = (value) => {
    if (!value) return '';
    value = value.toString();
    return value.charAt(0).toUpperCase() + value.slice(1);
};
export const timestamp2date = (value, dateFormat = 'dd.MM.yyyy') => {
    if (!_.isNumber(value) || value === null) return '';
    return format(appFromUnixTime(value), dateFormat, { locale: activeDateLocale() });
};
// Support time, as the tickets list and the ticket's own time panel both show
// it. Seconds are what the entries store; nobody reads support time in seconds.
// Rounded UP, because a started minute is a minute somebody spent.
export const supportTimeFormat = (seconds) => {
    const minutes = Math.ceil((seconds || 0) / 60);
    if (minutes < 60) return `${minutes} Min.`;
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')} Std.`;
};
export const date2timestamp = (value) => {
    return appGetUnixTime(parse(value, 'dd.MM.yyyy', appNow()));
};
export const formatDate = (value, pattern = 'dd.MM.yyyy') => {
    if (value === null) return '';
    return format(value, pattern, { locale: activeDateLocale() });
};
// Vue.filter('formatDate', formatDate);
export const multiselectfilter = (value) => {
    // console.log("multiselectfilter", value)
    return value;
};
export const selectfilter = (value) => {
    // console.log("selectfilter", value)
    return value;
};
export const isNumeric = (string) => {
    return string !== '' && Number.isFinite(+string);
};
export const cleanup = (value) => {
    // if ((typeof value === 'number' || typeof value === 'string') && !isNaN(Number(value))) return parseFloat(value);
    if (isNumeric(value)) return parseFloat(value);
    return value;
};
export const objectsize = (size) => _.size(size);

/**
 * What to call a ticket nobody has named yet.
 *
 * A timer opens its ticket before the call it is timing has been described, so
 * a subject may be missing. The fallback is passed in rather than hard-coded so
 * a caller can hand over a translated word.
 */
export const ticketSubject = (subject, fallback = 'Ohne Betreff') => String(subject || '').trim() || fallback;
Vue.filter('ticketSubject', ticketSubject);
